import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { getSocketBaseUrl } from "../api";
import { toast } from "react-toastify";

const SocketContext = createContext(null);

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [manualJobStates, setManualJobStates] = useState({}); // { channelId: { logs: [], isRunning: boolean } }
    const socketRef = useRef(null);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!token) return;

        const socketUrl = getSocketBaseUrl();
        
        // Initialize Socket
        const newSocket = io(socketUrl, {
            transports: ["websocket", "polling"],
            auth: { token },
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 2000
        });

        socketRef.current = newSocket;

        newSocket.on("connect", () => {
            console.log("Socket Connected:", newSocket.id);
            setIsConnected(true);
        });

        newSocket.on("disconnect", () => {
            console.log("Socket Disconnected");
            setIsConnected(false);
        });

        newSocket.on("connect_error", (err) => {
            console.error("Socket Connection Error:", err.message);
            // Optionally handle token expiration (e.g., if err.message === 'Authentication error')
        });

        // Global Notification Listener
        newSocket.on("notification", (data) => {
             // data: { type: 'info|warning|error|success', title, message }
             if (data.title && data.message) {
                 toast(
                     <div>
                         <div className="font-bold">{data.title}</div>
                         <div className="text-sm">{data.message}</div>
                     </div>, 
                     { type: data.type || "info" }
                 );
             }
        });

        // Manual Job State Tracker (Persistence)
        newSocket.on("etl_update", (data) => {
            // data: { id, message, status }
            // Check if it's a manual job (starts with manual_)
            if (data.id && typeof data.id === 'string' && data.id.startsWith("manual_")) {
                setManualJobStates(prev => {
                    const current = prev[data.id] || { logs: [], isRunning: false };
                    const newLogs = [...current.logs];
                    if (data.message) newLogs.push(data.message);
                    
                    let newRunning = current.isRunning;
                    if (data.status === 'running') newRunning = true;
                    if (data.status === 'stopped' || data.status === 'completed' || (data.message && data.message.includes('Exit code'))) newRunning = false;
                    // Also simple heuristic: if message contains "Error" or "Finished", maybe stop? 
                    // Better to rely on explicit status if backend sends it. 
                    // Backend sends 'running' on start, 'stopped' on stop. 
                    // Python script finish doesn't send explicit status event, mostly just log.
                    // We can infer stop if message contains "Exit code" or "Finished"
                    if (data.message && (data.message.includes("Exit code") || data.message.includes("Finished") || data.message.includes("BATCH COMPLETED"))) {
                         // Optional: strict status management might require backend update. 
                         // For now, let's trust explicit status or infer end.
                         newRunning = false;
                    }

                    return {
                        ...prev,
                        [data.id]: {
                            logs: newLogs,
                            isRunning: newRunning
                        }
                    };
                });
            }
        });

        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
        };
    }, []);

    return (
        <SocketContext.Provider value={{ socket, isConnected, manualJobStates, setManualJobStates }}>
            {children}
        </SocketContext.Provider>
    );
};
